/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { sort, alias } from '@ember/object/computed';
import { isNone } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';
import type Goal from 'linced-admin/models/goals/goal';
import type Subcategory from 'linced-admin/models/goals/subcategory';
import type Subject from 'linced-admin/models/goals/subject';
import type School from 'linced-admin/models/school';

// Proto: goals.GoalCategoryTemplate.
export default class Category extends Model {
  declare [Type]: 'goals/category';

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @attr('string')
  declare campusId: string | undefined; // TODO: implement when we have Campuses

  @attr('boolean', { defaultValue: false })
  declare isPublished: boolean;

  @belongsTo('goals/subject', { async: true, inverse: 'categories' })
  declare subjectId: AsyncBelongsTo<Subject>;

  @alias('subjectId')
  declare subject: AsyncBelongsTo<Subject>;

  @attr('boolean', { defaultValue: false })
  declare isPLP: boolean;

  @hasMany('goals/subcategory', { async: true, inverse: 'categoryId' })
  declare subcategories: AsyncHasMany<Subcategory>;

  @computed('subcategories.content.[]')
  get subcategoriesArray() {
    return resolved(this.subcategories);
  }

  subcategorySortOrder = Object.freeze(['sequence', 'label']);

  @sort('subcategoriesArray', 'subcategorySortOrder')
  declare sortedSubcategories: unknown[];

  @computed('sortedSubcategories.[]')
  get sortedBoundSubcategories() {
    const subcategories: unknown[] & { __category__?: Category } =
      this.sortedSubcategories.slice();
    subcategories.__category__ = this;
    return subcategories;
  }

  @hasMany('goals/goal', { async: true, inverse: 'categoryId' })
  declare goals: AsyncHasMany<Goal>;

  @computed('goals.content.[]')
  get goalsArray() {
    return resolved(this.goals);
  }

  // Goals directly under the category. A goal with no subcategory reads its
  // subcategory_id as null, so match on both.
  @computed('goalsArray.@each.subcategory_id')
  get filteredGoals() {
    return this.goalsArray.filter((goal) => isNone(goal.subcategory_id));
  }

  goalSortOrder = Object.freeze(['sequence', 'label']);

  @sort('filteredGoals', 'goalSortOrder')
  declare sortedGoals: unknown[];

  @computed('sortedGoals.[]')
  get sortedBoundGoals() {
    const goals: unknown[] & { __category__?: Category } =
      this.sortedGoals.slice();
    goals.__category__ = this;
    return goals;
  }

  @attr('number')
  declare updateTime: number | undefined;

  @attr('number')
  declare createTime: number | undefined;

  @attr('boolean', { defaultValue: false })
  declare isLocked: boolean;

  @attr('string')
  declare templateId: string | undefined; // TODO: implement once Templates are added

  @attr('number', { defaultValue: 0 })
  declare sequence: number;

  @attr('string', { defaultValue: '' })
  declare label: string;

  @attr('string', { defaultValue: '' })
  declare description: string;

  @attr('string', { defaultValue: '' })
  declare badgeMediaId: string;

  @alias('badgeMediaId')
  declare badgeMedia: string;

  @attr('string', { defaultValue: '' })
  declare badgeDescription: string;
}
